import React, {useEffect, useState} from "react";
import {observer} from "mobx-react";
import {Alert, Badge, Button, Card, Col, Row, Spinner} from "react-bootstrap";
import rootStore from "../../../../stores/rootStore.js";
import {PaymentElement, useElements, useStripe} from "@stripe/react-stripe-js";
import {useNavigate} from "react-router-dom";

function OrderPayment({onBack}) {
    const stripe = useStripe();
    const elements = useElements();
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const taxCertOrderStore = rootStore.taxCertOrderStore;
    const isStripe = taxCertOrderStore.paymentProvider === "Stripe";
    useEffect(() => {
        if (!taxCertOrderStore.clientSecret && isStripe) {
            taxCertOrderStore.getClientSecret().catch(() => {
                setError("We could not initialize payment. Please try again.");
            });
        }
    }, [isStripe]);

    const handleSubmit = async (event) => {
        setError(null);
        setSubmitting(true);
        event.preventDefault();
        try {
            if (!isStripe) {
                const result = await taxCertOrderStore.finalizeOrder(null);
                navigate(`/order/${result.newOrderId}`);
                return;
            }
            if (!stripe || !elements) {
                setError("Payment form is still loading. Please try again.");
                return;
            }
            const {error: submitError} = await elements.submit();
            if (submitError) {
                setError(submitError.message);
                return;
            }
            // Confirm the payment on the client side
            const {paymentIntent, error: stripeError} = await stripe.confirmPayment({
                elements,
                clientSecret:taxCertOrderStore.clientSecret,
                redirect: "if_required",
            });

            if (stripeError) {
                setError(stripeError.message);
            } else {
                if (!paymentIntent) {
                    setError("Payment was not completed. Please try again.");
                    return;
                }
                if (paymentIntent.status === "processing") {
                    setError("Payment is still processing. Please wait a moment and refresh.");
                    return;
                }
                if (paymentIntent.status !== "succeeded" && paymentIntent.status !== "requires_capture") {
                    setError(`Payment status: ${paymentIntent.status}. Please try again.`);
                    return;
                }
                try {
                    const result = await taxCertOrderStore.finalizeOrder(paymentIntent);
                    navigate(`/order/${result.newOrderId}`);
                } catch (e) {
                    setError("An error occurred while finalizing your payment. Please try again.");
                    console.error(e);
                    if (paymentIntent?.id) {
                        await taxCertOrderStore.invalidatePaymentIntent(paymentIntent.id);
                    }
                }
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Card className="shadow-sm order-payment-card">
            <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                <div>
                    <strong>Payment</strong>
                    <div className="text-muted small">
                        {isStripe ? "Secure checkout powered by Stripe" : "Payment will be charged to your LTSA deposit account"}
                    </div>
                </div>
                {taxCertOrderStore.total > 0 && (
                    <Badge bg="primary">
                        {taxCertOrderStore.total.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        })}
                    </Badge>
                )}
            </Card.Header>
            <Card.Body>
                <form onSubmit={handleSubmit}>
                    {isStripe ? (
                        <PaymentElement />
                    ) : (
                        <Alert variant="info" className="mb-3">
                            You are signed in with LTSA. This order will be billed to your LTSA deposit account.
                        </Alert>
                    )}
                    {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
                    <Row className="mt-4 g-2">
                        <Col xs={12} md="auto">
                            <Button type="submit" disabled={(isStripe && !stripe) || submitting}>
                                {submitting ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Processing...
                                    </>
                                ) : (
                                    (isStripe ? "Pay and Complete Order" : "Complete Order")
                                )}
                            </Button>
                        </Col>
                        {onBack && (
                            <Col xs={12} md="auto">
                                <Button type="button" variant="outline-secondary" onClick={onBack}>
                                    Back to Details
                                </Button>
                            </Col>
                        )}
                    </Row>
                </form>
            </Card.Body>
        </Card>
    )
}

export default observer(OrderPayment);
